/* eslint-disable react/jsx-one-expression-per-line */
import {
  ErrorOutline,
  FileOpenRounded,
  PaymentRounded,
} from '@mui/icons-material'
import classNames from 'classnames'
import dayjs from 'dayjs'
import { checkIfDateLessToday } from 'feature/appeals/helpers/checkIfDateLessToday'
import { stateCurrentAppeal } from 'feature/appeals/redux/appealFormCreateSelector'
import { TAppealVisit } from 'feature/appeals/type'
import { convertDataToStringWithMonth } from 'helpers/date/convertDataToStringWithMonth'
import { getTimeByTimeZone } from 'helpers/date/getTimeByTimeZone'
import { isMoscowTimeZone } from 'helpers/date/isMoscowTimeZone'
import { formatPriceInRubles } from 'helpers/formatPrice'
import { ReactNode } from 'react'
import { useAppSelector } from 'store/hooks'
import { Button } from 'ui'
import { DesktopMobileTooltip } from 'widget/DesktopMobileTooltip/DesktopMobileTooltip'
import { InformationBlock } from 'widget/InformationBlock/InformationBlock'

import style from './OrderVisit.module.scss'

type TOrderVisit = {
  visit: TAppealVisit
  timeSlotText: string
  children: ReactNode
  zeroPriceText: string
}

// информация о замере заявки

export function OrderVisit({
  visit,
  timeSlotText,
  children,
  zeroPriceText,
}: TOrderVisit) {
  const { offer, grade } = visit
  const { appealStatus } = useAppSelector(stateCurrentAppeal)

  const payDate = getTimeByTimeZone(offer?.payDate)
  const visitDate = getTimeByTimeZone(visit?.date || '')
  const expireDate = dayjs(visitDate).add(-1, 'day').format('YYYY-MM-DD')

  const isPaymentLinkActive = checkIfDateLessToday(visitDate)
  const isPaymentBlockVisible =
    visit.status !== 'PLANNED' &&
    !offer?.isFree &&
    (!!offer?.checkUrl || !!offer?.offerUrl || !!offer?.acquiringUrl)

  const isDeclined = appealStatus === 'CANCELED' || appealStatus === 'UNKNOWN'

  return (
    <section className={style.orderVisit}>
      <div className={style.visitInf}>
        <div className={style.visitDate}>
          <InformationBlock title={timeSlotText}>
            <span className={style.timeSlot}>
              {convertDataToStringWithMonth(visitDate, true)}
              {!isMoscowTimeZone() && (
                <DesktopMobileTooltip
                  title='По местному времени'
                  Icon={<ErrorOutline />}
                />
              )}
            </span>
          </InformationBlock>
        </div>

        {isPaymentBlockVisible && (
          <div className={style.payment}>
            <span className='h2'>Оплата</span>

            {visit.status === 'OFFER' && (
              <div className={style.paymentInf}>
                {expireDate && isPaymentLinkActive && (
                  <span className={classNames('caption', style.dateEnd)}>
                    {`
                    Срок оплаты до
                    ${convertDataToStringWithMonth(expireDate, false, false)}
                     включительно`}
                  </span>
                )}

                {!isPaymentLinkActive && (
                  <span className={classNames('caption', style.dateEnd)}>
                    Срок оплаты истек. Свяжитесь с дилером для изменения даты и
                    времени выезда специалиста.
                  </span>
                )}

                {offer?.offerUrl && (
                  <Button
                    //@ts-ignore
                    to={offer.offerUrl}
                    startIcon={<FileOpenRounded color='primary' />}
                    variant='contained'
                    color='secondary'
                    className={style.openCheck}
                    target='_blank'
                  >
                    Открыть счет на оплату
                  </Button>
                )}

                {offer?.price > 0 && isPaymentLinkActive && (
                  <>
                    <div className={style.price}>
                      <span className='default-text'>Стоимость услуги</span>
                      <span className='h2'>
                        {formatPriceInRubles(offer?.price || 0)}
                      </span>
                    </div>

                    {!isDeclined && (
                      <div className={style.buttonsWrapper}>
                        <Button
                          //@ts-ignore
                          to={offer.acquiringUrl}
                          target='_blank'
                          startIcon={<PaymentRounded color='secondary' />}
                          variant='contained'
                          color='primary'
                          className={style.openCheck}
                          disabled={!isPaymentLinkActive}
                        >
                          Оплатить
                        </Button>
                      </div>
                    )}
                  </>
                )}

                {offer?.price === 0 &&
                  !offer?.isFree &&
                  isPaymentLinkActive && (
                    <div className={style.price}>
                      <span className='default-text'>{zeroPriceText}</span>
                    </div>
                  )}
              </div>
            )}
            {visit.status === 'PAID' && (
              <div className={style.paymentInf}>
                {offer?.checkUrl && (
                  <Button
                    //@ts-ignore
                    to={offer.checkUrl}
                    target='_blank'
                    startIcon={<FileOpenRounded color='primary' />}
                    variant='contained'
                    color='secondary'
                    className={style.openCheck}
                  >
                    Открыть чек
                  </Button>
                )}

                {payDate && (
                  <>
                    <span className={style.createdDate}>
                      {`
                      Оплачено
                      ${convertDataToStringWithMonth(payDate, true)}
                      `}
                      {!isMoscowTimeZone() && (
                        <DesktopMobileTooltip
                          title='По местному времени'
                          Icon={<ErrorOutline />}
                        />
                      )}
                    </span>
                  </>
                )}
                {offer?.price !== null && (
                  <div className={style.price}>
                    <span className='default-text'>Стоимость услуги</span>
                    <span className='h2'>
                      {formatPriceInRubles(offer?.price || 0)}
                    </span>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {grade !== null && <>{children}</>}
    </section>
  )
}
